<template>
  <v-navigation-drawer
    v-model="isOpen"
    app
    right
    temporary
    width="320"
    class="theme-customizer"
    @keydown.esc.native="isOpen = false"
  >
    <!-- Header -->
    <div class="customizer-header d-flex align-center justify-space-between px-5 py-4">
      <div>
        <div class="customizer-title">{{ $t('themeCustomizer.title') }}</div>
        <div class="customizer-subtitle">{{ $t('themeCustomizer.subtitle') }}</div>
      </div>
      <div class="d-flex align-center">
        <v-btn
          icon
          small
          class="mr-1"
          :title="$t('themeCustomizer.reset')"
          :aria-label="$t('themeCustomizer.reset')"
          @click="reset"
        >
          <v-icon small>autorenew</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :title="$t('close')"
          :aria-label="$t('close')"
          @click="isOpen = false"
        >
          <v-icon small>close</v-icon>
        </v-btn>
      </div>
    </div>

    <v-divider />

    <div class="customizer-body px-5 pt-4 pb-6">

      <!-- ── Theming chip ── -->
      <div class="mb-4">
        <v-chip small color="primary" text-color="white" label class="font-weight-semibold px-3">
          {{ $t('themeCustomizer.theming') }}
        </v-chip>
      </div>

      <!-- Primary Color -->
      <div class="cust-section-label mb-3">{{ $t('themeCustomizer.primaryColor') }}</div>
      <div class="color-palette">
        <button
          v-for="c in colorPalette"
          :key="c.value"
          class="color-dot"
          :style="{ background: c.value }"
          :class="{ 'color-dot--active': settings.primaryColor === c.value }"
          :title="$t('themeCustomizer.colors.' + c.id)"
          @click="applyColor(c.value)"
        >
          <v-icon v-if="settings.primaryColor === c.value" x-small color="white">check</v-icon>
        </button>
        <!-- Custom picker -->
        <button
          class="color-dot color-dot--picker"
          :title="$t('themeCustomizer.customColor')"
          :aria-label="$t('themeCustomizer.customColor')"
          @click="openPicker"
        >
          <v-icon x-small>palette</v-icon>
          <input
            ref="colorPicker"
            type="color"
            class="hidden-color-input"
            :value="settings.primaryColor"
            @input="applyColor($event.target.value)"
          />
        </button>
      </div>

      <v-divider class="my-5" />

      <!-- Theme -->
      <div class="cust-section-label mb-3">{{ $t('themeCustomizer.theme') }}</div>
      <div class="theme-options">
        <button
          v-for="t in themeOptions"
          :key="t.value"
          class="theme-card"
          :class="{ 'theme-card--active': settings.themePreference === t.value }"
          @click="applyTheme(t.value)"
        >
          <v-icon class="theme-card-icon" :color="settings.themePreference === t.value ? 'primary' : ''">
            {{ t.icon }}
          </v-icon>
          <span class="theme-card-label">{{ $t('themeCustomizer.themes.' + t.value) }}</span>
        </button>
      </div>

      <v-divider class="my-5" />

      <!-- Skin -->
      <div class="cust-section-label mb-3">{{ $t('themeCustomizer.skin') }}</div>
      <div class="skin-options">
        <button
          v-for="s in skinOptions"
          :key="s.value"
          class="skin-card"
          :class="{ 'skin-card--active': settings.skin === s.value }"
          @click="applySkin(s.value)"
        >
          <div class="skin-mockup" :class="`skin-mockup--${s.value}`">
            <div class="sm-sidebar"></div>
            <div class="sm-main">
              <div class="sm-topbar"></div>
              <div class="sm-body">
                <div class="sm-row">
                  <div class="sm-card"></div>
                  <div class="sm-card"></div>
                </div>
                <div class="sm-block"></div>
              </div>
            </div>
          </div>
          <span class="skin-card-label">{{ $t('themeCustomizer.skins.' + s.value) }}</span>
        </button>
      </div>

    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import Vue from 'vue';
import {
    UiSettings,
    setUiPrimaryColor,
    setUiSkin,
    setUiThemePreference,
    resetUiSettings,
    ThemePreference,
    UiSkin,
} from '@/plugins/UiSettings';
import {Firestore} from '@/plugins/Firestore';
import {LocalStorage} from '@/plugins/LocalStorage';

export default Vue.extend({
    name: 'ThemeCustomizer',
    model: {
        prop: 'value',
        event: 'input',
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        settings: UiSettings,
        colorPalette: [
            { value: '#0770b8', id: 'blue' },
            { value: '#6eaa34', id: 'green' },
            { value: '#7367F0', id: 'violet' },
            { value: '#00BCD4', id: 'cyan' },
            { value: '#e67e22', id: 'orange' },
            { value: '#EA5455', id: 'red' },
        ],
        themeOptions: [
            { value: 'light' as ThemePreference, icon: 'light_mode' },
            { value: 'dark'  as ThemePreference, icon: 'dark_mode' },
            { value: 'system' as ThemePreference, icon: 'monitor' },
        ],
        skinOptions: [
            { value: 'default'  as UiSkin },
            { value: 'bordered' as UiSkin },
        ],
        mediaQuery: null as MediaQueryList | null,
        mediaListener: null as ((e: MediaQueryListEvent) => void) | null,
    }),
    computed: {
        isOpen: {
            get(): boolean { return this.value; },
            set(v: boolean) { this.$emit('input', v); },
        },
    },
    watch: {
        'settings.primaryColor': {
            immediate: true,
            handler(color: string) {
                this.$vuetify.theme.themes.light.primary = color;
                this.$vuetify.theme.themes.dark.primary = color;
            },
        },
        'settings.themePreference': {
            immediate: true,
            handler(pref: ThemePreference) {
                this.applyThemePreference(pref);
            },
        },
    },
    beforeDestroy() {
        this.removeSystemListener();
    },
    methods: {
        applyColor(color: string) {
            setUiPrimaryColor(color);
        },
        applyTheme(pref: ThemePreference) {
            setUiThemePreference(pref);
        },
        applySkin(skin: UiSkin) {
            setUiSkin(skin);
        },
        applyThemePreference(pref: ThemePreference) {
            this.removeSystemListener();
            if (pref === 'system') {
                this.setupSystemListener();
            } else {
                const dark = pref === 'dark';
                this.$vuetify.theme.dark = dark;
                LocalStorage.setTheme(dark);
                if ((this as any).$store?.getters?.authenticated) {
                    Firestore.setTheme(dark);
                }
            }
        },
        setupSystemListener() {
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            this.mediaQuery = mq;
            const listener = (e: MediaQueryListEvent) => {
                this.$vuetify.theme.dark = e.matches;
            };
            this.mediaListener = listener;
            mq.addEventListener('change', listener);
            // Apply immediately
            this.$vuetify.theme.dark = mq.matches;
        },
        removeSystemListener() {
            if (this.mediaQuery && this.mediaListener) {
                this.mediaQuery.removeEventListener('change', this.mediaListener);
                this.mediaQuery = null;
                this.mediaListener = null;
            }
        },
        openPicker() {
            (this.$refs.colorPicker as HTMLInputElement)?.click();
        },
        reset() {
            resetUiSettings();
        },
    },
});
</script>

<style lang="sass" scoped>
.theme-customizer
  ::v-deep .v-navigation-drawer__content
    display: flex
    flex-direction: column
    overflow-y: auto

.customizer-header
  flex-shrink: 0

.customizer-title
  font-size: 1rem
  font-weight: 600
  letter-spacing: 0.01em
  line-height: 1.4

.customizer-subtitle
  font-size: 0.78rem
  opacity: 0.6
  margin-top: 2px

.customizer-body
  flex: 1
  overflow-y: auto

.cust-section-label
  font-size: 0.8rem
  font-weight: 600
  letter-spacing: 0.06em
  text-transform: uppercase
  opacity: 0.7

// ── Color palette ──────────────────────────────────────────
.color-palette
  display: flex
  flex-wrap: wrap
  gap: 8px

.color-dot
  width: 36px
  height: 36px
  border-radius: 50%
  display: flex
  align-items: center
  justify-content: center
  cursor: pointer
  border: 3px solid transparent
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease
  position: relative
  outline: none

  &:hover
    transform: scale(1.12)
    box-shadow: 0 4px 12px rgba(0,0,0,0.25)

  &--active
    border-color: white !important
    box-shadow: 0 0 0 3px var(--v-primary-base)

  &--picker
    background: transparent !important
    border: 2px dashed rgba(0,0,0,0.25)

    .theme--dark &
      border-color: rgba(255,255,255,0.25)

.hidden-color-input
  position: absolute
  width: 0
  height: 0
  opacity: 0
  pointer-events: none

// ── Theme cards ─────────────────────────────────────────────
.theme-options
  display: flex
  gap: 10px

.theme-card
  flex: 1
  display: flex
  flex-direction: column
  align-items: center
  padding: 12px 8px 10px
  border-radius: 10px
  border: 2px solid transparent
  cursor: pointer
  transition: border-color 0.15s, background 0.15s
  outline: none

  .theme--light &
    background: rgba(0,0,0,0.04)
    border-color: rgba(0,0,0,0.06)

  .theme--dark &
    background: rgba(255,255,255,0.06)
    border-color: rgba(255,255,255,0.08)

  &:hover
    .theme--light &
      background: rgba(0,0,0,0.07)
    .theme--dark &
      background: rgba(255,255,255,0.1)

  &--active
    border-color: var(--v-primary-base) !important
    .theme--light &
      background: rgba(8, 135, 220, 0.08)
    .theme--dark &
      background: rgba(8, 135, 220, 0.15)

.theme-card-icon
  font-size: 22px !important

.theme-card-label
  font-size: 0.72rem
  font-weight: 500
  margin-top: 6px
  white-space: nowrap

// ── Skin cards ──────────────────────────────────────────────
.skin-options
  display: flex
  gap: 12px

.skin-card
  flex: 1
  display: flex
  flex-direction: column
  align-items: center
  cursor: pointer
  outline: none
  border: none
  background: transparent
  padding: 0

.skin-card-label
  font-size: 0.75rem
  font-weight: 500
  margin-top: 6px

// Mini mockup
.skin-mockup
  width: 100%
  height: 66px
  border-radius: 8px
  overflow: hidden
  display: flex
  border: 2px solid transparent
  transition: border-color 0.15s

  .skin-card--active &
    border-color: var(--v-primary-base)

  .theme--light &
    background: #f4f5fa

  .theme--dark &
    background: #2c2f3a

.sm-sidebar
  width: 22%
  height: 100%
  background: rgba(0,0,0,0.07)

  .theme--dark .sm-sidebar
    background: rgba(255,255,255,0.08)

.sm-main
  flex: 1
  display: flex
  flex-direction: column

.sm-topbar
  height: 20%
  background: rgba(0,0,0,0.07)
  margin: 4px 4px 0

  .theme--dark .sm-topbar
    background: rgba(255,255,255,0.08)

.sm-body
  flex: 1
  padding: 4px

.sm-row
  display: flex
  gap: 3px
  margin-bottom: 3px

.sm-card
  flex: 1
  height: 18px
  border-radius: 3px

.sm-block
  height: 11px
  border-radius: 3px

// Default skin: shadow cards
.skin-mockup--default
  .sm-card, .sm-block
    background: white
    box-shadow: 0 1px 3px rgba(0,0,0,0.15)

    .theme--dark &
      background: rgba(255,255,255,0.12)
      box-shadow: none

// Bordered skin: border cards
.skin-mockup--bordered
  .sm-card, .sm-block
    background: white
    border: 1px solid rgba(0,0,0,0.15)

    .theme--dark &
      background: rgba(255,255,255,0.06)
      border-color: rgba(255,255,255,0.12)
</style>
